import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  pageContentCardSx,
  pageFieldSx,
  pagePrimaryButtonSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";
import {
  createA3Articulo,
  deleteA3Articulo,
  getA3ArticuloPedidosUsage,
  getA3Caracteristicas,
  getA3FamiliasTalla,
  getA3Proveedores,
  searchA3Articulos,
  updateA3Articulo,
} from "../../api/Produccion/a3ArticulosApi";
import {
  A3_EMPRESA_DB_SOURCE_OPTIONS,
  getEmpresaA3Meta,
  normalizeEmpresaA3,
} from "../../constants/a3Empresas";

const formatDraftPrice = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const normalized = raw.replace(",", ".");
  const n = Number(normalized);
  if (!Number.isFinite(n)) return raw;
  return String(n);
};

const normalizeCodproKey = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  if (/^\d+$/.test(trimmed)) {
    const stripped = trimmed.replace(/^0+/, "");
    return stripped || "0";
  }
  return trimmed.toUpperCase();
};

const matchProveedorOption = (options, rawCodpro) => {
  const target = String(rawCodpro ?? "").trim();
  if (!target || !Array.isArray(options)) return null;
  const targetKey = normalizeCodproKey(target);
  return (
    options.find((item) => {
      const code = String(item?.CODPRO ?? "").trim();
      if (!code) return false;
      if (code === target) return true;
      return normalizeCodproKey(code) === targetKey;
    }) || null
  );
};

const ArticuloA3Dialog = ({
  open,
  onClose,
  suggestedCode = "",
  onResolved,
  onSaved,
  onDeleted,
  initialDraft = null,
  initialDbSource = "STAGE_DESIGN",
  startMode = "search",
  editRow = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const fieldSx = pageFieldSx(theme);
  const sectionCardSx = {
    ...pageContentCardSx(theme, accent),
    p: { xs: 1.25, md: 1.5 },
    mb: 1.5,
  };
  const modeButtonSx = (active) => ({
    fontWeight: 800,
    textTransform: "none",
    borderRadius: 999,
    px: 2,
    minHeight: 36,
    ...(active
      ? {
          bgcolor: alpha(accent, 0.14),
          color: theme.palette.primary.dark,
          border: "1px solid",
          borderColor: alpha(accent, 0.28),
          boxShadow: `0 6px 16px ${alpha(accent, 0.12)}`,
          "&:hover": { bgcolor: alpha(accent, 0.18) },
        }
      : {
          color: "text.secondary",
          border: "1px solid transparent",
          "&:hover": { bgcolor: alpha(accent, 0.06) },
        }),
  });

  const currencyOptions = ["EURO", "USD"];
  const [search, setSearch] = useState(suggestedCode || "");
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [pedidosUsage, setPedidosUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogMode, setDialogMode] = useState("search");
  const [showDraftInSearch, setShowDraftInSearch] = useState(false);
  const [draftFromExisting, setDraftFromExisting] = useState(false);
  const [existingCodart, setExistingCodart] = useState("");
  const [, setLoadingCatalogs] = useState(false);
  const [car1Options, setCar1Options] = useState([]);
  const [car2Options, setCar2Options] = useState([]);
  const [car3Options, setCar3Options] = useState([]);
  const [proveedorOptions, setProveedorOptions] = useState([]);
  const [familiaTallaOptions, setFamiliaTallaOptions] = useState([]);
  const [dbSource, setDbSource] = useState(() =>
    normalizeEmpresaA3(initialDbSource, "STAGE_DESIGN"),
  );
  const formEditedRef = useRef(false);
  const initialDraftAppliedRef = useRef(false);
  const proveedorSyncRef = useRef("");
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const patchNewArticulo = useCallback((updater) => {
    formEditedRef.current = true;
    setNewArticulo((prev) =>
      typeof updater === "function" ? updater(prev) : { ...prev, ...updater },
    );
  }, []);

  const [newArticulo, setNewArticulo] = useState({
    CODART: suggestedCode || "",
    CAR1: "",
    CAR2: "",
    CAR3: "",
    CODMON: "EURO",
    CODMONCOM: "USD",
    CODPRO: "",
    CODFAMTALLAH: "",
    CODFAMTALLAV: "",
    DESCART: "",
    FECALTA: today,
    PRCCOMPRA: "",
    PRCCOSTE: "",
    PRCVENTA: "",
    TALLAS: "F",
  });

  const normalizeDecimal = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return 0;
    const normalized = raw.replace(",", ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const pickRowValue = (row, keys = [], fallback = "") => {
    for (const key of keys) {
      if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
    }
    return fallback;
  };
  const normalizeA3Currency = (value, fallback = "EURO") => {
    const s = String(value || "")
      .trim()
      .toUpperCase();
    if (!s) return fallback;
    if (s === "USD" || s === "DOLAR" || s === "DOLARES" || s === "$") return "USD";
    if (s === "EUR" || s === "EURO" || s === "€") return "EURO";
    return fallback;
  };

  const loadRows = useCallback(
    async (q) => {
      setLoading(true);
      setError("");
      try {
        const data = await searchA3Articulos(q, dbSource);
        setRows(data.rows || []);
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [dbSource],
  );

  const loadCatalogs = useCallback(async () => {
    setLoadingCatalogs(true);
    try {
      const [c1, c2, c3, provs, fams] = await Promise.all([
        getA3Caracteristicas(1, dbSource),
        getA3Caracteristicas(2, dbSource),
        getA3Caracteristicas(3, dbSource),
        getA3Proveedores("", dbSource),
        getA3FamiliasTalla("", dbSource),
      ]);
      setCar1Options(Array.isArray(c1?.rows) ? c1.rows : []);
      setCar2Options(Array.isArray(c2?.rows) ? c2.rows : []);
      setCar3Options(Array.isArray(c3?.rows) ? c3.rows : []);
      setProveedorOptions(Array.isArray(provs?.rows) ? provs.rows : []);
      setFamiliaTallaOptions(Array.isArray(fams?.rows) ? fams.rows : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setCar1Options([]);
      setCar2Options([]);
      setCar3Options([]);
      setProveedorOptions([]);
      setFamiliaTallaOptions([]);
    } finally {
      setLoadingCatalogs(false);
    }
  }, [dbSource]);

  const upsertProveedorOption = useCallback((option) => {
    if (!option?.CODPRO) return;
    const code = String(option.CODPRO).trim();
    if (!code) return;
    setProveedorOptions((prev) => {
      if (matchProveedorOption(prev, code)) return prev;
      return [{ ...option, CODPRO: code }, ...prev];
    });
  }, []);

  const syncProveedorSelection = useCallback(
    async (codproRaw) => {
      const raw = String(codproRaw ?? "").trim();
      if (!raw) return;

      const applyResolvedCodpro = (resolved, option = null) => {
        const code = String(resolved).trim();
        if (!code) return;
        proveedorSyncRef.current = raw;
        if (option) upsertProveedorOption(option);
        setNewArticulo((prev) =>
          String(prev.CODPRO || "").trim() === code
            ? prev
            : { ...prev, CODPRO: code },
        );
      };

      const cachedHit = matchProveedorOption(proveedorOptions, raw);
      if (cachedHit) {
        applyResolvedCodpro(cachedHit.CODPRO, cachedHit);
        return;
      }

      try {
        const data = await getA3Proveedores(raw, dbSource, 25);
        const fetched = Array.isArray(data?.rows) ? data.rows : [];
        const fetchedHit = matchProveedorOption(fetched, raw);
        if (fetchedHit) {
          applyResolvedCodpro(fetchedHit.CODPRO, fetchedHit);
          return;
        }
      } catch {
        // Si falla la búsqueda puntual, dejamos el código en el select.
      }

      applyResolvedCodpro(raw, {
        CODPRO: raw,
        NOMBRE: `Proveedor ${raw}`,
      });
    },
    [dbSource, proveedorOptions, upsertProveedorOption],
  );

  const applyInitialDraft = useCallback(
    (draft) => {
      if (!draft || formEditedRef.current) return;
      setNewArticulo((prev) => ({
        ...prev,
        CODART:
          draft?.CODART !== undefined &&
          draft?.CODART !== null &&
          String(draft.CODART).trim() !== ""
            ? String(draft.CODART).trim()
            : prev.CODART,
        DESCART:
          draft?.DESCART !== undefined &&
          draft?.DESCART !== null &&
          String(draft.DESCART).trim() !== ""
            ? String(draft.DESCART).trim()
            : prev.DESCART,
        CODMON: normalizeA3Currency(draft?.CODMON, prev.CODMON || "EURO"),
        CODMONCOM: normalizeA3Currency(
          draft?.CODMONCOM,
          prev.CODMONCOM || "USD",
        ),
        PRCCOMPRA:
          draft?.PRCCOMPRA !== undefined &&
          draft?.PRCCOMPRA !== null &&
          String(draft.PRCCOMPRA).trim() !== ""
            ? formatDraftPrice(draft.PRCCOMPRA)
            : prev.PRCCOMPRA,
        PRCVENTA:
          draft?.PRCVENTA !== undefined &&
          draft?.PRCVENTA !== null &&
          String(draft.PRCVENTA).trim() !== ""
            ? formatDraftPrice(draft.PRCVENTA)
            : prev.PRCVENTA,
      }));
    },
    [],
  );

  // Solo al abrir el diálogo: no re-inicializar al cambiar dbSource ni loadRows.
  useEffect(() => {
    if (!open) {
      formEditedRef.current = false;
      initialDraftAppliedRef.current = false;
      proveedorSyncRef.current = "";
      return;
    }
    const empresa = normalizeEmpresaA3(initialDbSource, "STAGE_DESIGN");
    const cod = String(suggestedCode || "").trim();
    setDbSource(empresa);
    setSearch(cod);
    setDialogMode(
      startMode === "create" || startMode === "edit" ? "create" : "search",
    );
    setShowDraftInSearch(false);
    setDraftFromExisting(false);
    setExistingCodart("");
    setError("");
    setRows([]);
    setNewArticulo({
      CODART: cod,
      CAR1: "",
      CAR2: "",
      CAR3: "",
      CODMON: "EURO",
      CODMONCOM: "USD",
      CODPRO: "",
      CODFAMTALLAH: "",
      CODFAMTALLAV: "",
      DESCART: "",
      FECALTA: today,
      PRCCOMPRA: "",
      PRCCOSTE: "",
      PRCVENTA: "",
      TALLAS: "F",
    });
    initialDraftAppliedRef.current = false;
    if (initialDraft) {
      applyInitialDraft(initialDraft);
      initialDraftAppliedRef.current = true;
    }
    if (cod) {
      searchA3Articulos(cod, empresa)
        .then((data) => setRows(data.rows || []))
        .catch((e) => {
          setError(e?.response?.data?.error || e.message);
          setRows([]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init solo al abrir
  }, [open]);

  useEffect(() => {
    if (!open || !initialDraft || initialDraftAppliedRef.current) return;
    if (formEditedRef.current) return;
    applyInitialDraft(initialDraft);
    initialDraftAppliedRef.current = true;
  }, [open, initialDraft, applyInitialDraft]);

  useEffect(() => {
    if (!open || !initialDraft || !Array.isArray(proveedorOptions) || !proveedorOptions.length) {
      return;
    }
    const provNombre = String(initialDraft?.ProveedorNombre || "").trim().toLowerCase();
    if (!provNombre) return;
    setNewArticulo((prev) => {
      if (String(prev.CODPRO || "").trim()) return prev;
      const hit =
        proveedorOptions.find(
          (p) => String(p?.NOMBRE || "").trim().toLowerCase() === provNombre,
        ) ||
        proveedorOptions.find((p) =>
          String(p?.NOMBRE || "").trim().toLowerCase().includes(provNombre),
        );
      if (!hit?.CODPRO) return prev;
      return { ...prev, CODPRO: String(hit.CODPRO).trim() };
    });
  }, [open, initialDraft, proveedorOptions]);

  useEffect(() => {
    if (!open) return;
    loadCatalogs();
  }, [open, dbSource, loadCatalogs]);

  useEffect(() => {
    if (!open) return;
    const codpro = String(newArticulo.CODPRO || "").trim();
    if (!codpro || proveedorSyncRef.current === codpro) return;

    const hit = matchProveedorOption(proveedorOptions, codpro);
    if (hit) {
      const resolved = String(hit.CODPRO).trim();
      proveedorSyncRef.current = codpro;
      if (resolved !== codpro) {
        setNewArticulo((prev) => ({ ...prev, CODPRO: resolved }));
      }
      return;
    }

    if (!proveedorOptions.length) return;
    void syncProveedorSelection(codpro);
  }, [open, newArticulo.CODPRO, proveedorOptions, syncProveedorSelection]);

  const handleCreate = async () => {
    if (!newArticulo.CODART) {
      setError("CODART es obligatorio");
      return;
    }
    if (!String(newArticulo.CODPRO || "").trim()) {
      setError("Proveedor (CODPRO) es obligatorio en A3");
      return;
    }
    if (
      newArticulo.TALLAS === "T" &&
      !String(newArticulo.CAR1 || "").trim()
    ) {
      setError("Tipo (CAR1) es obligatorio para artículos con tallas en A3");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const codArtFinal = String(newArticulo.CODART || "").trim();
      const precioCompraNum = normalizeDecimal(newArticulo.PRCCOMPRA);
      const payload = {
        ...newArticulo,
        CODART: codArtFinal,
        // Reglas negocio CRM: CODALT y alias se rellenan con el propio código.
        CODALT: codArtFinal,
        ARTALIAS: codArtFinal,
        TALLAS: newArticulo.TALLAS === "T" ? "T" : "F",
        PRCCOMPRA: precioCompraNum,
        // Oculto en UI: mantenemos coste alineado al precio compra.
        PRCCOSTE: precioCompraNum,
        PRCVENTA: normalizeDecimal(newArticulo.PRCVENTA),
      };
      if (draftFromExisting && existingCodart) {
        await updateA3Articulo(existingCodart, payload, dbSource);
      } else {
        await createA3Articulo(payload, dbSource);
      }
      onSaved?.({
        ...payload,
        dbSource,
        updated: Boolean(draftFromExisting && existingCodart),
      });
      await loadRows(newArticulo.CODART || search);
      setDialogMode("search");
      setShowDraftInSearch(false);
      setDraftFromExisting(false);
      setExistingCodart("");
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setCreating(false);
    }
  };

  const fillDraftFromRow = (row = {}) => {
    const codart = pickRowValue(
      row,
      ["CODART", "codart", "REFERENCIA", "referencia"],
      "",
    );
    const desc = pickRowValue(
      row,
      ["DESCART", "descart", "DESCRIPCION", "descripcion"],
      "",
    );
    const codpro = String(
      pickRowValue(row, ["CODPRO", "codpro"], "") || "",
    ).trim();
    proveedorSyncRef.current = "";
    patchNewArticulo((prev) => ({
      ...prev,
      CODART: String(codart || "").trim() || prev.CODART || "",
      CAR1: pickRowValue(row, ["CAR1", "car1"], prev.CAR1 ?? ""),
      CAR2: pickRowValue(row, ["CAR2", "car2"], prev.CAR2 ?? ""),
      CAR3: pickRowValue(row, ["CAR3", "car3"], prev.CAR3 ?? ""),
      CODMON: normalizeA3Currency(
        pickRowValue(row, ["CODMON", "codmon"], prev.CODMON ?? "EURO"),
        prev.CODMON ?? "EURO",
      ),
      CODMONCOM: normalizeA3Currency(
        pickRowValue(row, ["CODMONCOM", "codmoncom"], prev.CODMONCOM ?? "USD"),
        prev.CODMONCOM ?? "USD",
      ),
      CODPRO: codpro || prev.CODPRO || "",
      CODFAMTALLAH: pickRowValue(
        row,
        ["CODFAMTALLAH", "codfamtallah"],
        prev.CODFAMTALLAH ?? "",
      ),
      CODFAMTALLAV: pickRowValue(
        row,
        ["CODFAMTALLAV", "codfamtallav"],
        prev.CODFAMTALLAV ?? "",
      ),
      DESCART: String(desc || "").trim() || prev.DESCART || "",
      FECALTA: pickRowValue(row, ["FECALTA", "fecalta"], prev.FECALTA ?? today),
      PRCCOMPRA: pickRowValue(
        row,
        ["PRCCOMPRA", "prccompra"],
        prev.PRCCOMPRA ?? "",
      ),
      PRCCOSTE: pickRowValue(
        row,
        ["PRCCOSTE", "prccoste"],
        prev.PRCCOSTE ?? "",
      ),
      PRCVENTA: pickRowValue(
        row,
        ["PRCVENTA", "prcventa"],
        prev.PRCVENTA ?? "",
      ),
      TALLAS: (() => {
        const raw = pickRowValue(row, ["TALLAS", "tallas"], prev.TALLAS ?? "F");
        const s = String(raw ?? "")
          .trim()
          .toUpperCase();
        if (s === "T" || s === "1" || raw === true || raw === 1) return "T";
        return "F";
      })(),
    }));
    setDraftFromExisting(Boolean(String(codart || "").trim()));
    setExistingCodart(String(codart || "").trim());
    if (codpro) {
      void syncProveedorSelection(codpro);
    }
  };

  useEffect(() => {
    if (!open || startMode !== "edit" || !editRow) return;
    fillDraftFromRow(editRow);
    setDialogMode("create");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir en edicion
  }, [open, startMode, editRow]);

  useEffect(() => {
    if (!open || !existingCodart || !draftFromExisting) {
      setPedidosUsage(null);
      return;
    }
    let cancelled = false;
    setLoadingUsage(true);
    void getA3ArticuloPedidosUsage(existingCodart, dbSource)
      .then((data) => {
        if (!cancelled) setPedidosUsage(data);
      })
      .catch(() => {
        if (!cancelled) setPedidosUsage(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsage(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, existingCodart, draftFromExisting, dbSource]);

  const canDeleteArticulo =
    Boolean(existingCodart) &&
    draftFromExisting &&
    pedidosUsage?.canDelete === true;

  const openDeleteDialog = () => {
    setDeleteError("");
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!existingCodart) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteA3Articulo(existingCodart, dbSource);
      setDeleteDialogOpen(false);
      onDeleted?.({ codart: existingCodart, dbSource });
      onClose?.();
    } catch (e) {
      setDeleteError(e?.response?.data?.error || e.message);
    } finally {
      setDeleting(false);
    }
  };

  const renderCreateForm = ({
    title = "Anadir articulo nuevo",
    withDivider = false,
  }) => (
    <>
      {withDivider ? <Divider sx={{ my: 2 }} /> : null}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {draftFromExisting && existingCodart
            ? `Editar artículo ${existingCodart}`
            : title}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            label={`Empresa ${getEmpresaA3Meta(dbSource).shortLabel}`}
            sx={{
              height: 24,
              fontWeight: 800,
              bgcolor: alpha(theme.palette.info.main, 0.12),
              color: theme.palette.info.dark,
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label="CODALT y Alias = Código artículo"
            sx={{ height: 24, fontWeight: 700 }}
          />
          <Chip
            size="small"
            label={
              newArticulo.TALLAS === "T"
                ? "Artículo con tallas"
                : "Artículo sin tallas"
            }
            sx={{
              height: 24,
              fontWeight: 800,
              ...(newArticulo.TALLAS === "T"
                ? {
                    bgcolor: alpha("#00a58a", 0.14),
                    color: "#007a66",
                    border: "1px solid",
                    borderColor: alpha("#00a58a", 0.28),
                  }
                : {
                    bgcolor: alpha(theme.palette.text.secondary, 0.08),
                    color: "text.secondary",
                  }),
            }}
          />
        </Stack>
      </Stack>

      <Box sx={sectionCardSx}>
        <Typography sx={{ ...pageSectionTitleSx, mb: 1.25 }}>
          Identificación
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(140px, 0.8fr) 2fr" },
            gap: 1.25,
          }}
        >
          <TextField
            required
            size="small"
            label="Código del artículo"
            value={newArticulo.CODART || ""}
            onChange={(e) => {
              const nextCodart = e.target.value;
              patchNewArticulo((p) => ({ ...p, CODART: nextCodart }));
              if (
                draftFromExisting &&
                String(nextCodart || "").trim() !==
                  String(existingCodart || "").trim()
              ) {
                setDraftFromExisting(false);
                setExistingCodart("");
              }
            }}
            sx={{
              ...fieldSx,
              "& .MuiInputBase-input": {
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontWeight: 700,
              },
            }}
          />
          <TextField
            size="small"
            label="Descripción del artículo"
            value={newArticulo.DESCART || ""}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, DESCART: e.target.value }))
            }
            sx={fieldSx}
          />
        </Box>
      </Box>

      <Box sx={sectionCardSx}>
        <Typography sx={{ ...pageSectionTitleSx, mb: 1.25 }}>
          Clasificación y tallas
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1.25,
          }}
        >
          <TextField
            required={newArticulo.TALLAS === "T"}
            size="small"
            label="Tipo"
            value={newArticulo.CAR1 || ""}
            select
            SelectProps={{ native: true }}
            onFocus={() => !car1Options.length && loadCatalogs()}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CAR1: e.target.value }))
            }
            sx={fieldSx}
          >
            <option value=""></option>
            {car1Options.map((item) => (
              <option key={`c1-${item.CODCAR}`} value={item.CODCAR}>
                {item.CODCAR} - {item.DESCCAR}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Material"
            value={newArticulo.CAR2 || ""}
            select
            SelectProps={{ native: true }}
            onFocus={() => !car2Options.length && loadCatalogs()}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CAR2: e.target.value }))
            }
            sx={fieldSx}
          >
            <option value=""></option>
            {car2Options.map((item) => (
              <option key={`c2-${item.CODCAR}`} value={item.CODCAR}>
                {item.CODCAR} - {item.DESCCAR}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Sub material"
            value={newArticulo.CAR3 || ""}
            select
            SelectProps={{ native: true }}
            onFocus={() => !car3Options.length && loadCatalogs()}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CAR3: e.target.value }))
            }
            sx={fieldSx}
          >
            <option value=""></option>
            {car3Options.map((item) => (
              <option key={`c3-${item.CODCAR}`} value={item.CODCAR}>
                {item.CODCAR} - {item.DESCCAR}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Familia talla horizontal (H)"
            value={newArticulo.CODFAMTALLAH || ""}
            select
            SelectProps={{ native: true }}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CODFAMTALLAH: e.target.value }))
            }
            sx={fieldSx}
          >
            <option value=""></option>
            {familiaTallaOptions.map((item) => (
              <option
                key={`fam-h-${item.CODFAMTALLA}`}
                value={item.CODFAMTALLA}
              >
                {item.CODFAMTALLA} - {item.DESCFAMTALLA}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Familia color vertical (V)"
            value={newArticulo.CODFAMTALLAV || ""}
            select
            SelectProps={{ native: true }}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CODFAMTALLAV: e.target.value }))
            }
            sx={fieldSx}
          >
            <option value=""></option>
            {familiaTallaOptions.map((item) => (
              <option
                key={`fam-v-${item.CODFAMTALLA}`}
                value={item.CODFAMTALLA}
              >
                {item.CODFAMTALLA} - {item.DESCFAMTALLA}
              </option>
            ))}
          </TextField>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.75),
              bgcolor: alpha(theme.palette.background.paper, 0.9),
            }}
          >
            <FormControlLabel
              sx={{ m: 0, width: "100%" }}
              control={
                <Switch
                  checked={newArticulo.TALLAS === "T"}
                  onChange={(e) =>
                    patchNewArticulo((p) => ({
                      ...p,
                      TALLAS: e.target.checked ? "T" : "F",
                    }))
                  }
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" fontWeight={700}>
                  Usar tallas
                </Typography>
              }
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...sectionCardSx, mb: 0 }}>
        <Typography sx={{ ...pageSectionTitleSx, mb: 1.25 }}>
          Comercial
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1.25,
          }}
        >
          <TextField
            required
            size="small"
            label="Proveedor"
            value={String(newArticulo.CODPRO || "").trim()}
            select
            SelectProps={{ native: true }}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CODPRO: e.target.value }))
            }
            sx={fieldSx}
          >
            <option value=""></option>
            {proveedorOptions.map((item) => {
              const codpro = String(item.CODPRO || "").trim();
              return (
                <option key={`prov-${codpro}`} value={codpro}>
                  {String(item.NOMBRE || "").trim()
                    ? `${item.NOMBRE} (${codpro})`
                    : codpro}
                </option>
              );
            })}
          </TextField>
          <TextField
            size="small"
            label="Moneda compra"
            value={newArticulo.CODMON || ""}
            select
            SelectProps={{ native: true }}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CODMON: e.target.value }))
            }
            sx={fieldSx}
          >
            {currencyOptions.map((c) => (
              <option key={`codmon-${c}`} value={c}>
                {c}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Moneda venta"
            value={newArticulo.CODMONCOM || ""}
            select
            SelectProps={{ native: true }}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, CODMONCOM: e.target.value }))
            }
            sx={fieldSx}
          >
            {currencyOptions.map((c) => (
              <option key={`codmoncom-${c}`} value={c}>
                {c}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Fecha alta"
            InputLabelProps={{ shrink: true }}
            value={newArticulo.FECALTA || ""}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, FECALTA: e.target.value }))
            }
            sx={fieldSx}
          />
          <TextField
            size="small"
            label="Precio compra"
            value={newArticulo.PRCCOMPRA}
            inputProps={{ inputMode: "decimal", placeholder: "0.00" }}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, PRCCOMPRA: e.target.value }))
            }
            sx={{
              ...fieldSx,
              "& .MuiInputBase-input": {
                fontWeight: 800,
                color: theme.palette.error.main,
              },
            }}
          />
          <TextField
            size="small"
            label="Precio venta"
            value={newArticulo.PRCVENTA}
            inputProps={{ inputMode: "decimal", placeholder: "0.00" }}
            onChange={(e) =>
              patchNewArticulo((p) => ({ ...p, PRCVENTA: e.target.value }))
            }
            sx={{
              ...fieldSx,
              "& .MuiInputBase-input": {
                fontWeight: 800,
                color: "#00a58a",
              },
            }}
          />
        </Box>
      </Box>
    </>
  );

  const showSaveAction =
    dialogMode === "create" || (dialogMode === "search" && showDraftInSearch);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: "92dvh",
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.8),
          boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.18)}`,
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 1.75 },
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.75),
          background: `linear-gradient(145deg, ${alpha(accent, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 70%)`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accent, 0.14),
              color: theme.palette.primary.dark,
              border: "1px solid",
              borderColor: alpha(accent, 0.24),
            }}
          >
            <Inventory2OutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...pageSectionTitleSx, fontSize: "0.62rem" }}>
              CRM · Producción · A3
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Artículo A3
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Busca un artículo existente o crea uno nuevo para producción.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent
        sx={{
          pt: 1.5,
          px: { xs: 1.5, md: 2 },
          pb: 1,
          bgcolor: alpha(theme.palette.background.default, 0.35),
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.75,
            p: 1,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.75),
            bgcolor: alpha(theme.palette.background.paper, 0.88),
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="text"
              onClick={() => setDialogMode("search")}
              sx={modeButtonSx(dialogMode === "search")}
            >
              Buscar
            </Button>
            <Button
              variant="text"
              onClick={() => setDialogMode("create")}
              sx={modeButtonSx(dialogMode === "create")}
            >
              Añadir
            </Button>
            <TextField
              size="small"
              select
              label="Empresa"
              value={dbSource}
              onChange={(e) => {
                const next = normalizeEmpresaA3(e.target.value, "STAGE_DESIGN");
                setDbSource(next);
                setRows([]);
                setDraftFromExisting(false);
                setExistingCodart("");
                const q = String(search || "").trim();
                if (q) {
                  setLoading(true);
                  searchA3Articulos(q, next)
                    .then((data) => setRows(data.rows || []))
                    .catch((err) => {
                      setError(err?.response?.data?.error || err.message);
                      setRows([]);
                    })
                    .finally(() => setLoading(false));
                }
              }}
              SelectProps={{ native: true }}
              sx={{ ...fieldSx, minWidth: 180 }}
            >
              {A3_EMPRESA_DB_SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </TextField>
          </Stack>
          {dialogMode === "search" ? (
            <Chip
              size="small"
              label={showDraftInSearch ? "Formulario visible" : "Solo búsqueda"}
              onClick={() => setShowDraftInSearch((prev) => !prev)}
              sx={{
                height: 26,
                fontWeight: 800,
                cursor: "pointer",
                ...(showDraftInSearch
                  ? {
                      bgcolor: alpha(accent, 0.12),
                      color: theme.palette.primary.dark,
                    }
                  : {}),
              }}
            />
          ) : null}
        </Box>

        {dialogMode === "search" ? (
          <>
            <Typography sx={{ ...pageSectionTitleSx, mb: 1 }}>
              Buscar artículo en A3
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Código o descripción"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    loadRows(search);
                  }
                }}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={() => loadRows(search)}
                disabled={loading}
                sx={{
                  fontWeight: 800,
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2.5,
                  whiteSpace: "nowrap",
                  borderColor: alpha(accent, 0.35),
                  color: accent,
                }}
              >
                Buscar
              </Button>
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>
              Resultados ({rows.length})
            </Typography>
            <List
              dense
              sx={{
                maxHeight: 220,
                overflow: "auto",
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.8),
                borderRadius: 2.5,
                mb: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.96),
                "& .MuiListItemButton-root": {
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                  "&:last-child": { borderBottom: 0 },
                  "&:hover": {
                    bgcolor: alpha(accent, 0.08),
                  },
                },
              }}
            >
              {rows.map((row, idx) => (
                <ListItemButton
                  key={idx}
                  onClick={() => {
                    if (showDraftInSearch) {
                      fillDraftFromRow(row);
                      return;
                    }
                    onResolved?.({
                      ...row,
                      CODART: String(
                        pickRowValue(row, ["CODART", "codart"], ""),
                      ).trim(),
                      dbSource: String(dbSource || "STAGE_DESIGN").trim(),
                    });
                  }}
                >
                  <ListItemText
                    primary={String(
                      row.CODART || row.REFERENCIA || row.ID || "Articulo",
                    ).trim()}
                    secondary={row.DESCART || row.NOMBRE || ""}
                    primaryTypographyProps={{
                      fontWeight: 800,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  />
                </ListItemButton>
              ))}
              {!rows.length ? (
                <Box sx={{ p: 2.5, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    No hay coincidencias.
                  </Typography>
                </Box>
              ) : null}
            </List>

            {showDraftInSearch
              ? renderCreateForm({
                  title: "Borrador editable desde búsqueda",
                  withDivider: true,
                })
              : null}
          </>
        ) : (
          renderCreateForm({
            title: "Añadir artículo nuevo",
            withDivider: false,
          })
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.25,
          borderTop: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.75),
          bgcolor: theme.palette.background.paper,
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Box>
          {startMode === "edit" && draftFromExisting && existingCodart ? (
            <Tooltip
              title={
                loadingUsage
                  ? t("articulosA3.deleteChecking")
                  : pedidosUsage?.canDelete === false
                    ? t("articulosA3.deleteBlockedPedidos", {
                        count: pedidosUsage?.pedidosLineas ?? 0,
                      })
                    : t("articulosA3.deleteTooltip")
              }
            >
              <span>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={openDeleteDialog}
                  disabled={creating || deleting || loadingUsage || !canDeleteArticulo}
                  sx={{ fontWeight: 800, textTransform: "none" }}
                >
                  {t("articulosA3.delete")}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={onClose}
            disabled={creating || deleting}
            sx={{ fontWeight: 700, textTransform: "none", color: "text.secondary" }}
          >
            Cerrar
          </Button>
          {showSaveAction ? (
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={creating || deleting}
              sx={{ ...pagePrimaryButtonSx(theme), minWidth: 180 }}
            >
              {draftFromExisting && existingCodart
                ? "Actualizar en A3"
                : "Crear en A3"}
            </Button>
          ) : null}
        </Stack>
      </DialogActions>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("articulosA3.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {t("articulosA3.deleteConfirm", { codart: existingCodart })}
          </DialogContentText>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {t("articulosA3.deleteWarning")}
          </Alert>
          {deleteError ? (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            {t("articulosA3.deleteCancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ fontWeight: 800 }}
          >
            {deleting ? t("articulosA3.deleting") : t("articulosA3.deleteConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ArticuloA3Dialog;
